import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ContextMenuCustomItemProps, ContextMenuItemConfig } from '@milanote/roma';
import { getDisplayName } from '../../../../common/users/userHelper';
import type { ReactI18nextTranslationFunction } from '../../../../common/utils/i18n/coreTranslationHelpers';
import friendlyTimeTranslated from '../../../../common/utils/i18n/friendlyTimeTranslated';
import { getTimestamp } from '../../../../common/utils/timeUtil';
import { useTranslator } from '../../../components/i18n/useTranslator';
import { UserAvatarV2 } from '../../../components/userAvatar/UserAvatarV2';
import type { ElementContextMenuContext } from '../context/elementContextMenuContext';
import {
    buildElementMetadataDisplayRows,
    hasResolvableMetadataAuthor,
    type ElementMetadataDisplayRow,
} from './elementMetadataUtils';

/** Renders one translated metadata sentence for one user. */
const MetadataDisplayRow: React.FC<{
    row: ElementMetadataDisplayRow;
    formatTime: (timestamp: number) => string;
}> = ({ row, formatTime }) => {
    const translate = useTranslator('canvas', 'contextMenuItems.modification');
    const translationContext = {
        ...(row.isCurrentUser ? { context: 'you' } : { userName: getDisplayName(row.user) }),
        createdTime: row.createdTimestamp === undefined ? undefined : formatTime(row.createdTimestamp),
        modifiedTime: row.modifiedTimestamp === undefined ? undefined : formatTime(row.modifiedTimestamp),
    };
    const components = {
        authorName: <span className="whitespace-nowrap" />,
        createdTime: <span className="whitespace-nowrap" />,
        modifiedTime: <span className="whitespace-nowrap" />,
    };

    return (
        <div className="flex min-w-0 flex-1 items-center gap-md">
            <UserAvatarV2 user={row.user} size="sm" />
            <div className="min-w-0 flex-1 text-sm leading-4.5 text-tertiary text-balance">
                {translate(row.kind, translationContext, { components })}
            </div>
        </div>
    );
};

type ElementMetadataMenuItemProps = ContextMenuCustomItemProps<ElementContextMenuContext>;

/** Renders one metadata row for each resolved author. */
const ElementMetadataMenuItem: React.FC<ElementMetadataMenuItemProps> = ({ context }) => {
    const { t, ready } = useTranslation('common');
    if (!ready) return null;

    const fromTimestamp = getTimestamp();
    const formatTime = (timestamp: number) =>
        friendlyTimeTranslated({
            t: t as unknown as ReactI18nextTranslationFunction,
            timestamp,
            fromTimestamp,
            locale: context.locale,
        });
    const rows = buildElementMetadataDisplayRows({
        metadata: context.metadata,
        users: context.users,
        currentUserId: context.currentUserId,
        formatTime,
    });
    if (!rows.length) return null;

    return (
        <div className="min-w-0 flex flex-col gap-md py-xs">
            {rows.map((row) => (
                <MetadataDisplayRow key={row.kind} row={row} formatTime={formatTime} />
            ))}
        </div>
    );
};

export const elementMetadataMenuItems: ContextMenuItemConfig<ElementContextMenuContext>[] = [
    {
        type: 'custom',
        id: 'metadata-modification',
        localeKey: '',
        // Availability must mirror the rendered rows, otherwise an unresolvable author
        // leaves an empty section (divider plus blank disabled row) in the menu.
        availabilityPredicate: hasResolvableMetadataAuthor,
        isDisabled: true,
        component: ElementMetadataMenuItem,
    },
];
